import { GridColDef, GridCellParams, GridSortModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { Clear, Download } from '@mui/icons-material';
import { renderCellExpand } from 'src/modules/groupMessageEmail/components/renderCellExpand';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useCallback, useEffect, useState } from 'react';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { getAllDocuments } from 'src/shared/services/documentService';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { downloadDocumentFile } from 'src/shared/functions/downloadDocumentFile';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { buildDownloadName } from 'src/shared/functions/fileExtensions';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { DocumentSearchFilter } from '../models/documentSearchFilter';
import { DocumentResponse } from '../models/documentResponse';
import { DocumentIkonHandler } from './documentIkonHandler';
import { DocumentOverviewGridFooterButton } from './documentOverviewGridFooterButton';

type DocumentArchiveDataGridProps = {
  searchFilter: DocumentSearchFilter;
  searchFilterCallback: (searchFilter: DocumentSearchFilter) => void;
  refetchDataGrid: boolean;
  setRefetchDataGrid: (setRefetch: boolean) => void;
  setDocumentIdDetails: (documentId: number) => void;
};

export const DocumentArchiveDataGrid = (props: DocumentArchiveDataGridProps) => {
  const { searchFilter, searchFilterCallback, refetchDataGrid, setRefetchDataGrid, setDocumentIdDetails } = props;

  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [rows, setRows] = useState<DocumentResponse[]>([]);
  const [downloadingFile, setDownloadingFile] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });
  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const onRowClick = (params) => {
    if (params.row && params.row.document_id > 0) setDocumentIdDetails(params.row.document_id);
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }

    try {
      const result = await getAllDocuments(searchFilter);
      const documentResult = extendObjectWithId(result.data.results, 'document_id');
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          const withCategoryFallback = documentResult.map((item) => ({
            ...item,
            documentCategory: item.documentCategory_id === 0 ? t('documentArchive:other') : item.documentCategory,
          }));
          setRows(withCategoryFallback);
        }, 100);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      updateData('loading', false);
      setRefetchDataGrid(false);
    }
  }, [isMounted, searchFilter, setRefetchDataGrid, t]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  useEffect(() => {
    if (isMounted() && refetchDataGrid) {
      fetchData();
    }
  }, [fetchData, isMounted, refetchDataGrid]);

  const downloadCallback = useCallback((isLoading: boolean) => {
    setDownloadingFile(isLoading);
  }, []);

  const downloadFile = async (id: number, fileName: string, fallbackType?: string) => {
    await downloadDocumentFile(id, fileName, downloadCallback, fallbackType);
  };

  const columns: GridColDef[] = [
    {
      field: 'contentType',
      headerName: '',
      align: 'center',
      sortable: true,
      flex: 0.7,
      renderCell: (params: GridCellParams) => {
        return <DocumentIkonHandler contentType={params.row.contentType} />;
      },
    },
    {
      field: 'label',
      headerName: t('documentArchive:documentTitle'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams<DocumentResponse>) => {
        return (
          <Typography sx={{ fontWeight: 500, lineHeight: 1.2 }} variant='body1'>
            {params.row.label}
          </Typography>
        );
      },
    },
    {
      field: 'fileName',
      headerName: t('documentArchive:fileName'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams<DocumentResponse>) => {
        return params.row.fileName;
      },
    },

    {
      field: 'description',
      flex: 2,
      headerName: t('documentArchive:documentCategory.description'),
      renderCell: renderCellExpand,
    },
    {
      field: 'private',
      flex: 0.7,
      headerName: t('documentArchive:visibility'),
      renderCell: (params: GridCellParams<DocumentResponse>) => {
        return params.row.private ? t('documentArchive:private') : t('documentArchive:public');
      },
    },
    {
      field: 'user_id',
      flex: 1.3,
      headerName: t('documentArchive:uploadedBy'),
      renderCell: (params: GridCellParams<DocumentResponse>) => {
        return params.row.user_id;
      },
    },
    {
      field: 'created',
      headerName: t('documentArchive:created'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams<DocumentResponse>) => {
        return DateHelper.getDate(params.row.created);
      },
    },
    {
      field: 'download',
      headerName: ' ',
      sortable: false,
      minWidth: 130,
      flex: 0.7,
      renderCell: (params: GridCellParams<DocumentResponse>) => (
        <Button
          endIcon={<Download fontSize='small' />}
          sx={{ whiteSpace: 'nowrap' }}
          onClick={(e) => {
            e.stopPropagation();
            const fileName = buildDownloadName(params.row.contentType, params.row.fileName);
            downloadFile(params.row.document_id, fileName, params.row.contentType);
          }}
        >
          {t('file:downloadButton')}
        </Button>
      ),
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
          py: 1,
        },
      }}
      ref={scrollRef}
    >
      <LoadingOverlay message={t('file:downloadInProgress')} loading={downloadingFile} />
      <DataGrid<DocumentResponse>
        autoHeight
        columns={columns}
        rows={rows}
        onRowClick={onRowClick}
        dataModel={data}
        disableSelectionOnClick
        checkboxSelection
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          ConsoleHelper.log('newSortModel', newSortModel);
          const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
        selectionModel={selectionModel}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
      />
      {selectionModel && selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }} size='large'>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            <DocumentOverviewGridFooterButton selectionModel={selectionModel} fetchData={fetchData} setSelectionModel={setSelectionModel} />
          </Box>
        </FixedFooter>
      )}
    </Box>
  );
};
